import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useMutation } from '@tanstack/react-query';
import { useQueryCacheResourceHelpers } from '../utils/useQueryCacheResourceHelpers.js';
import { useWoody } from '../../woody/woody.js';
import { toast } from '../../store/alerts.js';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { resourceQueryPredicates } from '../queries/resourceQueryPredicates.js';

const useMutationMoveResourcesById = () => {
  const { client } = useWoody();
  const { track } = useAnalytics();
  const { deleteCachedResources, addNewResourcesToCache } = useQueryCacheResourceHelpers();
  const accessToken = useContextAccessToken();
  const mutation = useMutation({
    mutationFn: async ({ resourceIds, newParent }) => {
      return client.v2({
        endpoint: "/v2/resources/{destinationParentId}/move",
        params: {
          destinationParentId: newParent.id
        }
      }, {
        method: "put",
        body: {
          resourceIds
        },
        query: {
          accessToken
        }
      });
    },
    onMutate: ({ resourceIds, newParent }) => {
      const deleteCachedResourcesContext = deleteCachedResources(resourceIds, {
        listPredicate: (q) => resourceQueryPredicates.resourceContextSearchList(q) || resourceQueryPredicates.resourceList(q)
      });
      const mutatedResources = deleteCachedResourcesContext.deletedResources.map((resource) => {
        return resourceIds.includes(resource.id) ? {
          ...resource,
          parent: newParent
        } : resource;
      });
      const addNewResourcesToCacheContext = addNewResourcesToCache(mutatedResources);
      return {
        deleteCachedResourcesContext,
        addNewResourcesToCacheContext
      };
    },
    onSuccess: (_, { resourceIds, action = "move-modal" }) => {
      track(AnalyticsEvents.MovedItems, {
        action,
        count: resourceIds.length
      });
      const movedItemsTotal = resourceIds.length || 0;
      toast({
        content: movedItemsTotal > 1 ? `${movedItemsTotal} items moved` : "Item moved"
      });
    },
    onError: (_, _v, context) => {
      toast({
        content: "Failed to move"
      });
      context?.deleteCachedResourcesContext.resetCacheToPreOptimisticState();
      context?.deleteCachedResourcesContext.invalidateQueries();
      context?.addNewResourcesToCacheContext.resetCacheToPreOptimisticState();
      context?.addNewResourcesToCacheContext.invalidateQueries();
    },
    onSettled: (_, _e, _v, ctx) => {
      ctx?.addNewResourcesToCacheContext.clearPendingState();
    }
  });
  return mutation;
};

export { useMutationMoveResourcesById };
//# sourceMappingURL=useMutationMoveResourcesById.js.map
